import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router'
import { scrollToElement } from '@/lib/scroll'
import type { NavigationItem } from '@/constants/index'

type Props = {
    item: NavigationItem
}

export function NavDropdown({ item }: Props) {
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const closeTimerRef = useRef<number | null>(null)

    // Close on Escape
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    // Close on outside click
    useEffect(() => {
        if (!open) return
        const onClick = (e: MouseEvent) => {
            const t = e.target as Node
            if (panelRef.current?.contains(t)) return
            if (triggerRef.current?.contains(t)) return
            setOpen(false)
        }
        window.addEventListener('mousedown', onClick)
        return () => window.removeEventListener('mousedown', onClick)
    }, [open])

    const cancelClose = () => {
        if (closeTimerRef.current !== null) {
            window.clearTimeout(closeTimerRef.current)
            closeTimerRef.current = null
        }
    }

    const scheduleClose = () => {
        cancelClose()
        closeTimerRef.current = window.setTimeout(() => setOpen(false), 150)
    }

    const handleItemClick = (href: string, external?: boolean) => {
        setOpen(false)
        if (external) return // let anchor default fire
        // Support both "#section" and "/path#section" formats.
        const hashIndex = href.indexOf('#')
        const path = hashIndex === -1 ? href : href.slice(0, hashIndex)
        const hash = hashIndex === -1 ? '' : href.slice(hashIndex)

        // Cross-page hash link: navigate to the path; ScrollToHash will
        // handle the in-page scroll once the destination route mounts.
        if (path && path !== window.location.pathname) {
            navigate(path + hash)
            return
        }

        // Same-page hash link: scroll directly (no router work needed).
        if (hash) {
            scrollToElement(hash.slice(1))
            return
        }

        // Internal route — use react-router for SPA navigation (no full reload)
        if (href.startsWith('/')) {
            navigate(href)
        }
    }

    if (!item.dropdown) return null
    const groups = item.dropdown.groups
    const hasCta = !!item.dropdown.cta

    return (
        <li
            className="relative"
            onMouseEnter={() => {
                cancelClose()
                setOpen(true)
            }}
            onMouseLeave={scheduleClose}
        >
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                className="relative z-10 cursor-pointer px-4 py-2 text-sm font-medium text-foreground whitespace-nowrap flex items-center gap-1.5 hover:text-foreground/80 transition-colors"
            >
                {item.name}
                <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''
                        }`}
                />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        ref={panelRef}
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                        role="menu"
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                        className={`!absolute top-full left-1/2 -translate-x-1/2 mt-3 ${hasCta ? 'w-[680px]' : 'w-[420px]'
                            } max-w-[92vw] bg-popover rounded-2xl border border-border/80 shadow-2xl p-5 z-50`}
                    >
                        <div
                            className={`grid gap-5 ${hasCta ? 'grid-cols-3' : 'grid-cols-1'
                                }`}
                        >
                            {/* Groups */}
                            <div
                                className={`${hasCta ? 'col-span-2' : 'col-span-1'} space-y-5`}
                            >
                                {groups.map((group, gi) => (
                                    <div key={gi}>
                                        {group.title && (
                                            <p className="font-body text-[10px] uppercase tracking-widest text-muted-foreground mb-2 px-2">
                                                {group.title}
                                            </p>
                                        )}
                                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                            {group.items.map((entry) => {
                                                const Icon = entry.icon
                                                return (
                                                    <li key={entry.label} role="none">
                                                        <a
                                                            href={entry.href}
                                                            target={
                                                                entry.external
                                                                    ? '_blank'
                                                                    : undefined
                                                            }
                                                            rel={
                                                                entry.external
                                                                    ? 'noopener noreferrer'
                                                                    : undefined
                                                            }
                                                            role="menuitem"
                                                            onClick={(e) => {
                                                                if (!entry.external) {
                                                                    e.preventDefault()
                                                                }
                                                                handleItemClick(entry.href, entry.external)
                                                            }}
                                                            className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-accent/60 transition-colors group"
                                                        >
                                                            <span className="w-9 h-9 rounded-lg border border-border bg-background flex items-center justify-center text-foreground/80 group-hover:text-primary group-hover:border-primary/40 transition-colors shrink-0">
                                                                <Icon className="w-4 h-4" />
                                                            </span>
                                                            <span className="min-w-0">
                                                                <span className="block font-body text-sm font-medium text-foreground">
                                                                    {entry.label}
                                                                </span>
                                                                <span className="block font-body text-xs text-muted-foreground font-light leading-snug mt-0.5">
                                                                    {entry.description}
                                                                </span>
                                                            </span>
                                                        </a>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            {/* CTA panel */}
                            {item.dropdown.cta && (
                                <a
                                    href={item.dropdown.cta.href}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        handleItemClick(item.dropdown!.cta!.href)
                                    }}
                                    className="col-span-1 rounded-xl border border-border bg-card hover:border-primary/40 transition-colors p-4 flex flex-col justify-between gap-3 group"
                                >
                                    <div>
                                        <p className="font-body text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                                            {item.dropdown.cta.eyebrow}
                                        </p>
                                        <p className="font-heading text-base text-foreground leading-snug mb-1.5">
                                            {item.dropdown.cta.title}
                                        </p>
                                        <p className="font-body text-xs text-muted-foreground font-light">
                                            {item.dropdown.cta.body}
                                        </p>
                                    </div>
                                    <span className="font-body text-xs text-primary group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                                        Explore →
                                    </span>
                                </a>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </li>
    )
}
